import React from "react"
import { Modal, Form, Col, Button, Row, Table, Container, Image } from "react-bootstrap"
import { toast } from "react-toastify"
import ActionButton from "../../ActionButton"
import { APIError } from "../../../api/ObservableFromFetch"
import SubscriptionPlan from "../../../api/Administration/Model/SubscriptionPlan"
import SubscriptionPermissionsGet, { PermissionsOfSubscriptionPlanGet, NotAssignedPermissionsOfSubscriptionPlanGet, Permission, UnassignedSubscriptionPermissionsGet } from "../../../api/Administration/Permissions"
import { forkJoin, Subscription } from "rxjs"
import GetSubscriptionPlans from "../../../api/Administration/GetSubscriptionPlans"
import { FaAngleDoubleDown, FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleDoubleUp, FaAngleDown, FaAngleUp, FaChevronLeft, FaChevronRight, FaPencilAlt } from "react-icons/fa"
import FormatDate from "../../../formatters/FormatDate"
import DatePicker from 'react-datepicker'
import { parseISO, format } from "date-fns"
import { BirthdayInput } from "../../FormattedInputs/BirthdayInput"
import { SubscriptionPlanPermissionSave } from "../../../api/Administration/Permissions"
import SubscriptionPlanCreate from "../../../api/Administration/SubscriptionPlanCreate"

interface PermissionsSubscriptionModalProps {
  onCancel: () => void
  userName?: string
}

interface PermissionsSubscriptionModalState {
  loading: boolean
  saving: boolean
  selectedSubscription?: SubscriptionData
  editPermission?: EditPermission
  subscriptions: SubscriptionPlan[]
  permissions: Permission[]
  unassigned: Permission[]
  assigned: Permission[]
  notassigned: Permission[]
  selectionLeft: number[]
  selectionRight: number[]
  showAddNewModal: boolean
  showEditModal: boolean
  newSubscription?: SubscriptionData
}

export interface EditPermission {
  ID?: number
  ActiveDate?: Date
  InactiveDate?: Date
  Title?: string
}

interface SubscriptionData {
  Description?: string
  SubscriptionPlanID?: number
  Title?: string
}

export default class PermissionsSubscriptionModal extends React.PureComponent<PermissionsSubscriptionModalProps, PermissionsSubscriptionModalState> {
  state: PermissionsSubscriptionModalState = {
    loading: false,
    saving: false,
    selectedSubscription: undefined,
    editPermission: undefined,
    subscriptions: [],
    permissions: [],
    unassigned: [],
    assigned: [],
    notassigned: [],
    selectionLeft: [],
    selectionRight: [],
    showAddNewModal: false,
    showEditModal: false,
    newSubscription: undefined
  }

  initialLoadSubscriptions?: Subscription
  loadSubscription?: Subscription
  updateSubscription?: Subscription
  activedate = React.createRef<DatePicker>()
  inactivedate = React.createRef<DatePicker>()

  componentDidMount() {
    this.loadInitialData()
  }

  loadInitialData() {
    this.initialLoadSubscriptions && this.initialLoadSubscriptions.unsubscribe()
    this.setState({ loading: true })
    this.initialLoadSubscriptions = forkJoin({
      subscriptions: GetSubscriptionPlans(),
      permissions: SubscriptionPermissionsGet(),
      unassigned: UnassignedSubscriptionPermissionsGet()
    })
      .subscribe(
        ({ subscriptions, permissions, unassigned }) => {
          const subscriptionPlanID = this.state.selectedSubscription?.SubscriptionPlanID
          this.setState({ subscriptions: [...subscriptions], permissions: [...permissions], unassigned: [...unassigned], loading: false, selectedSubscription: undefined, selectionLeft: [], selectionRight: [] })
          if (subscriptionPlanID) this.selectSubscription(subscriptionPlanID)
        },
        (error) => {
          this.handleAPIError(error, { loading: false })
        }
      )
  }

  componentWillUnmount() {
    this.initialLoadSubscriptions && this.initialLoadSubscriptions.unsubscribe()
    this.loadSubscription && this.loadSubscription.unsubscribe()
    this.updateSubscription && this.updateSubscription.unsubscribe()
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  selectSubscription = (id: number) => {
    const subs = this.state.subscriptions.find(item => item.SubscriptionPlanID === id)
    if (subs) {
      this.setState({ selectedSubscription: {Title: subs.Title, Description: subs.Description, SubscriptionPlanID: subs.SubscriptionPlanID} })

      this.loadSubscription && this.loadSubscription.unsubscribe()
      this.loadSubscription = forkJoin({
        assigned: PermissionsOfSubscriptionPlanGet(subs?.SubscriptionPlanID || 0, subs?.Title || ""),
        notassigned: NotAssignedPermissionsOfSubscriptionPlanGet(subs?.SubscriptionPlanID || 0, subs?.Title || "")
      })
        .subscribe(
          ({ assigned, notassigned }) => this.setState({ assigned: [...assigned], notassigned: [...notassigned], loading: false }),
          (error) => {
            this.handleAPIError(error, { loading: false })
          }
        )
    }
    else {this.setState({ selectedSubscription: undefined })}
  }

  handleAddNewCancel = () => this.setState({ showAddNewModal: false, newSubscription: undefined })
  handleEditCancel = () => this.setState({ showEditModal: false, editPermission: undefined })
  handleSubmit = () => {
    this.updateSubscription && this.updateSubscription.unsubscribe()
    this.setState({ saving: true })
    const { selectedSubscription, assigned } = this.state

    const subscriptionPlanID = selectedSubscription?.SubscriptionPlanID || 0
    const subscriptionPlanDescription = selectedSubscription?.Description || ""
    this.updateSubscription = SubscriptionPlanPermissionSave(subscriptionPlanID, subscriptionPlanDescription, this.props.userName || "", assigned).subscribe(
      data => {
        this.setState({ saving: false })
        toast.success(data)
        this.loadInitialData()
      },
      error => this.handleAPIError(error, { saving: false })
    )
  }
  handleAddNewSubmit = () => {
    this.updateSubscription && this.updateSubscription.unsubscribe()
    this.setState({ saving: true })
    const { newSubscription } = this.state

    this.updateSubscription = SubscriptionPlanCreate(newSubscription?.Title || "", newSubscription?.Description || "", this.props.userName || "").subscribe(
      data => {
        this.setState({ saving: false, showAddNewModal: false, loading: true })
        toast.success(data)
        GetSubscriptionPlans().subscribe(
          data => this.setState({ subscriptions: data, loading: false, selectedSubscription: undefined }),
          error => this.handleAPIError(error, { loading: false })
        )
      },
      error => this.handleAPIError(error, { saving: false })
    )
  }
  handleEditSubmit = () => {
    const { editPermission, assigned } = this.state
    const relatedPermission = assigned.find(item => item.PermissionID === editPermission?.ID)

    if (relatedPermission) {
      relatedPermission.ActiveDate = format(editPermission?.ActiveDate || 0, "yyyy-MM-dd'T'HH:mm:ss")
      relatedPermission.InActiveDate = format(editPermission?.InactiveDate || 0, "yyyy-MM-dd'T'HH:mm:ss")
    }

    this.setState({ showEditModal: false, editPermission: undefined })
  }
  handleAddNew = () => this.setState({ showAddNewModal: true })
  handleEdit = (permission: Permission) => this.setState({ showEditModal: true, editPermission: { ID: permission.PermissionID, Title: permission.Title, ActiveDate: parseISO(permission.ActiveDate || ""), InactiveDate: parseISO(permission.InActiveDate || "") } })

  addNewValid = () => {
    const { newSubscription } = this.state
    return !!newSubscription && !!newSubscription.Title && !!newSubscription.Description
   }
  editValid = () => {
    const { editPermission } = this.state
    return !!editPermission && !!editPermission.ActiveDate && !!editPermission.InactiveDate
  }
  validSubmit = () => {
    const { selectedSubscription } = this.state
    return !!selectedSubscription && !!selectedSubscription.Description
  }


  handleActiveDateChange = (date: Date) => {
    const inactiveDate = this.state.editPermission?.InactiveDate
    const dateiInPast: boolean = inactiveDate instanceof Date && inactiveDate < new Date
    const allowChange = inactiveDate instanceof Date ? date < inactiveDate || dateiInPast : true
    this.setState({ editPermission: { ...this.state.editPermission, ActiveDate: allowChange ? date : undefined, InactiveDate: dateiInPast ? undefined : inactiveDate } })
  }

  handleInActiveDateChange = (date: Date) => {
    const activeDate = this.state.editPermission?.ActiveDate
    const allowChange = activeDate instanceof Date ? date > activeDate : true
    this.setState({ editPermission: { ...this.state.editPermission, InactiveDate: allowChange ? date : undefined } })
  }

  move(dir: "ltr" | "rtl", all?: boolean) {
    if (dir === "ltr") {
      const ids = !!all ? this.state.notassigned.map(item => item.PermissionID) : this.state.selectionLeft
      this.setState({
        selectionLeft: [],
        notassigned: this.state.notassigned.filter(item => !ids.includes(item.PermissionID)),
        assigned: [...this.state.assigned, ...this.state.notassigned.filter(item => ids.includes(item.PermissionID))]
      })
    } else {
      const ids = !!all ? this.state.assigned.map(item => item.PermissionID) : this.state.selectionRight
      this.setState({
        selectionRight: [],
        notassigned: [...this.state.notassigned, ...this.state.assigned.filter(item => ids.includes(item.PermissionID))],
        assigned: this.state.assigned.filter(item => !ids.includes(item.PermissionID))
      })
    }
  }

  handleRightItemClick = (id: number) => {
    if (this.state.selectionRight.includes(id)) {
      this.setState({ selectionRight: this.state.selectionRight.filter(item => item !== id) })
    } else {
      this.setState({ selectionRight: [...this.state.selectionRight, id] })
    }
  }

  render() {
    const { loading, saving, selectedSubscription, editPermission, subscriptions, selectionLeft, selectionRight, showAddNewModal, showEditModal, newSubscription } = this.state
    const left = !selectedSubscription ? this.state.unassigned : this.state.notassigned
    const right = !selectedSubscription ? [] : this.state.assigned
    const disabled = !selectedSubscription || loading

    return (
      <>
        <Modal.Dialog size="xl">
          <Modal.Header>
            <Modal.Title className="text-uppercase">Subscription/Permission</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <React.Fragment>
              <Form.Row className="form-group">
                <Form.Label column sm={2} htmlFor="subscriptionPlan">Subscription</Form.Label>
                <Col sm={4}>
                  <Form.Control id="subscriptionPlan" as="select" value={selectedSubscription?.SubscriptionPlanID?.toString()} onChange={(e) => this.selectSubscription(Number(e.target.value))}>
                    <option value="">Select Subscription</option>
                    {subscriptions && subscriptions.map((plan: SubscriptionPlan) => <option value={plan.SubscriptionPlanID} key={plan.SubscriptionPlanID}>{plan.Title}</option>)}
                  </Form.Control>
                </Col>
              </Form.Row>
              <Form.Row className="form-group">
                <Form.Label column sm={2} htmlFor="subscriptionDesc">Description</Form.Label>
                <Col sm={9}>
                  <Form.Control as="textarea"
                    rows={4}
                    isInvalid={selectedSubscription && !selectedSubscription.Description}
                    disabled={!selectedSubscription}
                    value={selectedSubscription?.Description || ""}
                    onChange={(e) => this.setState({ selectedSubscription: {...selectedSubscription, Description: e.target.value}})} />
                </Col>
              </Form.Row>
              <hr />
              <Form className="px-4" hidden={loading}>
                <Row className="d-none d-lg-flex">
                  <Col lg={4}>Permissions Not Assigned</Col>
                  <Col lg={1} className="px-0"></Col>
                  <Col lg={7} className="pl-lg-0">Permissions Assigned</Col>
                </Row>
                <Row>
                  <Col className="d-lg-none">Permissions Not Assigned</Col>
                  <Col lg={4} className="pr-lg-0">
                    <Form.Control as="select" multiple className="grey-selection" style={{ height: "288px", boxShadow: "none" }}
                      disabled={false}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { this.setState({ selectionLeft: Array.from(e.target.selectedOptions, option => Number(option.value)) }) }}>
                      {left.map(item => <option key={item.PermissionID} value={item.PermissionID}>{item.Title}</option>)}
                    </Form.Control>
                  </Col>
                  <Col lg={1} className="px-0">
                    <Container fluid>
                      <Row>
                        <Col className="px-1 text-center">
                          <Button className="btn-sm btn-sm-icon mb-lg-2 mt-lg-0 my-1" disabled={!left.length || disabled} onClick={() => this.move("ltr", true)}>
                            <FaAngleDoubleRight className="d-none d-lg-inline" />
                            <FaAngleDoubleDown className="d-lg-none" />
                          </Button>
                        </Col>
                        <Col className="px-1 text-center">
                          <Button className="btn-sm btn-sm-icon mb-lg-2 mt-lg-0 my-1" disabled={!selectionLeft.length || disabled} onClick={() => this.move("ltr")}>
                            <FaChevronRight className="d-none d-lg-inline" />
                            <FaAngleDown className="d-lg-none" />
                          </Button>
                        </Col>
                        <Col className="px-1 text-center">
                          <Button className="btn-sm btn-sm-icon mb-lg-2 mt-lg-0 my-1" disabled={!selectionRight.length || disabled} onClick={() => this.move("rtl")}>
                            <FaChevronLeft className="d-none d-lg-inline" />
                            <FaAngleUp className="d-lg-none" />
                          </Button>
                        </Col>
                        <Col className="px-1 text-center">
                          <Button className="btn-sm btn-sm-icon mb-lg-2 mt-lg-0 my-1" disabled={!right.length || disabled} onClick={() => this.move("rtl", true)}>
                            <FaAngleDoubleLeft className="d-none d-lg-inline" />
                            <FaAngleDoubleUp className="d-lg-none" />
                          </Button>
                        </Col>
                      </Row>
                    </Container>
                  </Col>
                  <Col className="d-lg-none">Permissions Assigned</Col>
                  <Col lg={7} className="pl-lg-0" style={{ height: "288px", overflowY: "auto" }}>
                    <Table className="table-sm dark" borderless responsive>
                      <thead>
                        <tr>
                          <th>Permission</th>
                          <th>Status</th>
                          <th>Active</th>
                          <th>Inactive</th>
                          <th>Edit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {right.map(item => <tr key={item.PermissionID} className={selectionRight.includes(item.PermissionID) ? "selected" : ""} onClick={() => this.handleRightItemClick(item.PermissionID)}>
                          <td>{item.Title}</td>
                          <td>{item.RecordStatusType}</td>
                          <td>{FormatDate(item.ActiveDate)}</td>
                          <td>{FormatDate(item.InActiveDate)}</td>
                          <td>
                            <Button variant="icon-link" size="sm" className="btn-icon-primary" onClick={() => { this.handleEdit(item) }}>
                              <FaPencilAlt />
                            </Button>
                          </td>
                        </tr>)}
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </Form>
            </React.Fragment>
          </Modal.Body>
          <Modal.Footer>
            <ActionButton variant="primary" onClick={this.handleAddNew} disabled={!!this.state.selectedSubscription} >Add New</ActionButton>
            <Button variant="secondary" className="mr-2" onClick={() => this.props.onCancel()}>Cancel</Button>
            <ActionButton variant="primary" onClick={this.handleSubmit} disabled={loading || !this.validSubmit()} spin={saving} disableOnSpin>Submit</ActionButton>
          </Modal.Footer>
        </Modal.Dialog>

        <Modal show={showAddNewModal} onHide={() => this.handleAddNewCancel()} size="lg" backdrop="static" dialogClassName="dark">
          <Modal.Header>
            <Modal.Title className="text-uppercase">Add New Subscription</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Row className="form-group required">
              <Form.Label column lg={3} htmlFor="newName">Subscription </Form.Label>
              <Col lg={9}>
                <Form.Control id="newName" type="text"
                  value={newSubscription?.Title}
                  isInvalid={newSubscription && !newSubscription.Title}
                  onChange={(e) => this.setState({ newSubscription: { ...newSubscription, Title: e.target.value } })} />
              </Col>
            </Form.Row>
            <Form.Row className="form-group required">
              <Form.Label column lg={3} htmlFor="newDescription">Description</Form.Label>
              <Col lg={9}>
                <Form.Control as="textarea"
                  rows={4}
                  isInvalid={newSubscription && !newSubscription.Description}
                  value={newSubscription?.Description}
                  onChange={(e) => this.setState({ newSubscription: { ...newSubscription, Description: e.target.value } })} />
              </Col>
            </Form.Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" className="mr-2" onClick={() => this.handleAddNewCancel()}>Cancel</Button>
            <ActionButton variant="primary" onClick={() => this.handleAddNewSubmit()} disabled={!this.addNewValid()} spin={saving} disableOnSpin>Next</ActionButton>
          </Modal.Footer>
        </Modal>

        {!!editPermission && <Modal show={showEditModal} onHide={() => this.handleEditCancel()} size="lg" backdrop="static" dialogClassName="dark">
          <Modal.Header>
            <Modal.Title className="text-uppercase">Edit Subscription</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Row className="form-group required">
              <Form.Label column lg={2} htmlFor="editSubs" className="required">Subscription</Form.Label>
              <Col lg={4}>
                <Form.Control id="editSubs" type="text" defaultValue={selectedSubscription?.Title} disabled={true} />
              </Col>
              <Col lg={1}></Col>
              <Form.Label column lg={2} htmlFor="editActive">Active Date</Form.Label>
              <Col lg={3}>
                <DatePicker className="form-control"
                  ref={this.activedate}
                  required={true}
                  showMonthDropdown
                  showYearDropdown
                  minDate={new Date()}
                  dropdownMode="select"
                  popperPlacement="right"
                  selected={editPermission?.ActiveDate}
                  enableTabLoop={false}
                  onKeyDown={(e) => { if (this.activedate && this.activedate.current && e.key === 'Tab') { this.activedate.current.setOpen(false) } }}
                  onChange={date => this.handleActiveDateChange(date as Date)}
                  customInput={<BirthdayInput />} />
              </Col>
            </Form.Row>
            <Form.Row className="form-group required">
              <Form.Label column lg={2} htmlFor="editPerm" className="required">Permission</Form.Label>
              <Col lg={4}>
                <Form.Control id="editPerm" type="text" defaultValue={editPermission?.Title} disabled={true} />
              </Col>
              <Col lg={1}></Col>
              <Form.Label column lg={2} htmlFor="editInactive">Inactive Date</Form.Label>
              <Col lg={3}>
                <DatePicker className="form-control"
                  ref={this.inactivedate}
                  required={true}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  popperPlacement="right"
                  minDate={editPermission?.ActiveDate instanceof Date ? editPermission?.ActiveDate : new Date()}
                  selected={editPermission?.InactiveDate}
                  enableTabLoop={false}
                  onKeyDown={(e) => { if (this.inactivedate && this.inactivedate.current && e.key === 'Tab') { this.inactivedate.current.setOpen(false) } }}
                  onChange={date => this.handleInActiveDateChange(date as Date)}
                  customInput={<BirthdayInput />} />
              </Col>
            </Form.Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" className="mr-2" onClick={() => this.handleEditCancel()}>Cancel</Button>
            <ActionButton variant="primary" onClick={() => this.handleEditSubmit()} disabled={!this.editValid()} spin={saving} disableOnSpin>Finish</ActionButton>
          </Modal.Footer>
        </Modal>}
      </>
    )
  }
}
